import React, { useState, useEffect, useRef } from "react";
import { CircularProgress } from "@mui/material";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { useAuth } from "../../auth";
import isUserLoggedIn from "./helpers/redirect";
import { useNavigate, useLocation } from "react-router-dom";
import ClinicalClientDetailsTableRowClinicalRoute from "./tables/ClinicalClientDetailsTableRowClinicalRoute";
import ClientDetailsViewClinicalRoute from "./component/clients/ClientDetailsViewClinicalRoute";
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Pagination,
  PaginationItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import jwtDecode from "jwt-decode";
import Cookies from "js-cookie";
import { NoDataSvg } from "./clinicalNotesClinicalRoute/ClinicalNoteSVGs";
import { isAusDomain } from "./clinicalNotesClinicalRoute/lib/utils";
import { Search, Close } from "@mui/icons-material";

dayjs.extend(localizedFormat);

const ExpertClinicalClientDB = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const storedData = localStorage.getItem("Authorization");
  const token = storedData ? storedData?.split("Bearer ")[1] : null;
  const headers = {
    "X-API-Key": process.env.REACT_APP_EXPERT_API_KEY,
    "Content-Type": "application/json",
    token: token,
    authenticationToken: Cookies.get("authDataJwt"),
  };

  const [showClientDetails, setShowClientDetails] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [clientDetails, setClientDetails] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  let userID;

  //Use effect to open Client Details
  useEffect(() => {
    if (
      window.location.href.split("?")[1] !== undefined &&
      window.location.href.split("?")[1].includes("id=")
    ) {
      const searchParams = new URLSearchParams(location.search);

      window.location.href.split("?")[1].includes("id=");
      const clientIDFromURL = searchParams.get("id");
      const clientNameFromURL = searchParams.get("client");

      setShowClientDetails(true);
      let [clientItem] = clientList?.filter(
        (item) => item.user_id === clientIDFromURL
      );

      setClientDetails({
        id: clientItem?.user_id || clientIDFromURL,
        name: clientItem?.name || clientNameFromURL,
        image: clientItem?.image,
        email: clientItem?.email,
        phone: clientItem?.phone,
      });


    } else {
      setShowClientDetails(false);
    }
  }, [window.location.search, clientList]);

  //Fetch Clients
  useEffect(() => {
    if (!currentUser) return;

    postClientsData();
  }, [page]);

  // useEffect(() => {
  //   return () => {
  //     if (searchTimeoutRef.current) {
  //       clearTimeout(searchTimeoutRef.current);
  //     }
  //   };
  // }, []);

  const postClientsData = async (retryCount = 0) => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = Date.now();
      const response = await fetch(
        `${
          process.env.REACT_APP_EXPERT_BASE_URL
        }jwt-clinicalnotes/list-clients?professional_id=${
          currentUser?.UserId
        }&count=10&page=${page || 1}&_t=${cacheBuster}`,
        {
          method: "GET",
          headers,
        }
      );
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      const postResponse = await response.json();

      if (!postResponse.data) {
        throw new Error("No data received from API");
      }

      const clients = await postResponse.data;
      const decodedClients = jwtDecode(clients);
      
      // Validate decoded data
      if (!Array.isArray(decodedClients)) {
        throw new Error("Invalid client data format");
      }
      
      setClientList(decodedClients);
      setTotalPages(postResponse.total_pages);

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching clients:", error);
      
      // Retry logic for failed requests
      if (retryCount < 2) {

        setTimeout(() => postClientsData(retryCount + 1), 1000);
        return;
      }
      
      // Set empty list if all retries failed
      setClientList([]);
      setIsLoading(false);
    }
  };

  const handleRowClick = (name, id, email, sessions, image) => {

    const queryParam = `client=${name}&id=${id}`;
    const currentPath = window.location.pathname;
    const updatedPath = `${currentPath}?${queryParam}`;

    setClientDetails({
      name: name,
      id: id,
      email: email,
      sessions: sessions,
      image: image,
    });
    navigate(updatedPath);
    setShowClientDetails(true);
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleSearchOpen = () => {
    setOpenSearchDialog(true);
  };

  const handleSearchClose = () => {
    setOpenSearchDialog(false);
    setSearchQuery("");
    setHasSearched(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await fetch(
        "https://cauth.unitedwecare.com/users/admin/search-client",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
            "x-api-key": process.env.REACT_APP_EXPERT_API_KEY,
          },
          body: JSON.stringify({
            search: searchQuery,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }
      const postResponse = await response.json();
      setSearchResults(postResponse.data || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <>
      <div className="my-3 ClientDashboard">
        <div className="d-flex justify-content-between align-items-center">
          <div role="presentation">
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                onClick={() => {
                  navigate("/clinical/clients");
                  setClientDetails({});
                }}
                role="button"
                id="clients-breadcrumb"
                sx={{
                  fontWeight: "500",
                  fontSize: "20px",
                  textDecoration: "none",
                  color: "text.primary",
                }}
                className={`d-flex align-items-center justify-content-between gap-2 ${
                  clientDetails?.name && "text-secondary"
                }`}
              >
                {isAusDomain() ? "Patients" : "Clients"}
              </Link>

              {clientDetails?.name && (
                <Typography
                  sx={{ fontWeight: "500", fontSize: "20px" }}
                  color="text.primary"
                >
                  {clientDetails?.name}
                </Typography>
              )}
            </Breadcrumbs>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleSearchOpen}
              style={{
                borderRadius: "30px",
                backgroundColor: "#F3F4F5",
                padding: "5px 25px 5px 10px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                border: "1px solid lightblue",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <Search />
              Search
            </button>
          </div>

          <Dialog
            open={openSearchDialog}
            onClose={handleSearchClose}
            fullWidth
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                height: "60vh",
              },
            }}
          >
            <DialogContent
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  placeholder={`Search ${
                    isAusDomain() ? "Patients" : "Clients"
                  }`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    } else if (e.key === "Escape") {
                      handleSearchClose();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Search
                        sx={{
                          color: "text.secondary",
                          mr: 1,
                        }}
                      />
                    ),
                    endAdornment: (
                      <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={isSearching || !searchQuery.trim()}
                        sx={{
                          width: "max-content",
                          borderRadius: "8px",
                          textTransform: "none",
                          backgroundColor: "var(--primary-color)",
                          "&:hover": {
                            backgroundColor: "var(--primary-color)",
                            opacity: 0.9,
                          },
                        }}
                      >
                        {isSearching ? "Searching..." : "Search"}
                      </Button>
                    ),
                    sx: {
                      borderRadius: "8px",
                      backgroundColor: "#f5f6f7",
                      "& fieldset": { border: "none" },
                      "&:hover fieldset": { border: "none" },
                      "&.Mui-focused fieldset": { border: "none" },
                      padding: "0px 12px",
                      "& .MuiInputBase-input": {
                        padding: "12px 0px",
                      },
                    },
                  }}
                />
              </Box>
              {isSearching && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={20}
                    sx={{ ml: 1, color: "var(--primary-color)" }}
                  />
                </Box>
              )}
              {!hasSearched && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    No recent searches
                  </Typography>
                </Box>
              )}

              {hasSearched && searchResults.length === 0 && !isSearching && (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    No {isAusDomain() ? "patients" : "clients"} found
                  </Typography>
                </Box>
              )}

              {hasSearched && searchResults.length > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    mt: 2,
                    mx: -3,
                    px: 3,
                    pb: 2,
                  }}
                >
                  {searchResults.map((result) => (
                    <Box
                      key={result.user_id}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#f5f6f7",
                          cursor: "pointer",
                        },
                        mb: 1,
                      }}
                      onClick={() => {
                        handleRowClick(
                          result.name,
                          result.user_id,
                          result.email,
                          0,
                          result.image
                        );
                        handleSearchClose();
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {result.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {result.email}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
          </Dialog>
        </div>
        {showClientDetails && (
          <ClientDetailsViewClinicalRoute clientDetails={clientDetails} />
        )}

        <>
          {!showClientDetails &&
            (isLoading ? (
              <TableContainer
                className=" border rounded-3 "
                sx={{ marginTop: "20px" }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {/* <TableCell>Profile</TableCell> */}
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from(new Array(10)).map((_, index) => (
                      <TableRow
                        key={index}
                        className={`ClientDetails--TableRow`}
                        sx={{
                          backgroundColor: "#FFF",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell className="Table--Name">
                          <div>
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem", width: "5vw" }}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="Table--Sessions">
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "1rem", width: "15vw" }}
                          />
                        </TableCell>
                        <TableCell className="Table--Sessions">
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "1rem", width: "5vw" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <div className="Table--Actions__Alt d-flex">
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem", width: "5vw" }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <>
                <TableContainer
                  className=" border rounded-3 "
                  sx={{ marginTop: "20px" }}
                >
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        {/* <TableCell>Profile</TableCell> */}
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Sessions</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientList?.length === 0 ? (
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            <div className="NoDataAvailable">
                              {NoDataSvg}
                              <p>
                                No {isAusDomain() ? "Patients" : "Clients"}{" "}
                                Found
                              </p>
                            </div>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ) : (
                        clientList.map((item) => {

                          
                          // Use the fields as they are - don't swap them
                          const emailField = item.email || "";
                          const phoneField = item.phone || "";
                          
                          // Only show email if it's actually an email (contains @)
                          const finalEmail = emailField && emailField.includes("@") ? emailField : "";
                          const finalPhone = phoneField;
                          
                          return (
                            <ClinicalClientDetailsTableRowClinicalRoute
                              key={item.user_id}
                              id={item.user_id}
                              name={item.name}
                              email={finalEmail}
                              phone={finalPhone}
                              image={item.image}
                              sessions={item.session_count}
                              onClickCallback={handleRowClick}
                            />
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div className="d-flex justify-content-end mb-5 p-4">
                  <Box
                    sx={{
                      "& .MuiPaginationItem-root": {
                        "&.Mui-selected": {
                          backgroundColor: "var(--primary-color)", // Selected page background color
                          color: "#fff", // Selected page text color
                        },
                      },
                    }}
                  >
                    <Pagination
                      count={totalPages ? totalPages : 1}
                      page={page}
                      onChange={handleChange}
                      renderItem={(item) => <PaginationItem {...item} />}
                    />
                  </Box>
                </div>
              </>
            ))}
        </>
      </div>
    </>
  );
};

export default ExpertClinicalClientDB;
